/**
 * Необоротні активи v2 (рішення власника 2026-08-27): єдиний перелік НА,
 * історія картки, «Відомості про ОЗ», звіти, платформенні довідники.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { AssetKind } from './assets';

export interface AssetRegistryItem {
  kind: AssetKind;
  id: string;
  organization_id: string;
  inventory_number: string;
  name: string;
  group_name: string | null;
  account_code: string | null;
  method_name: string | null;
  primary_cost: string;
  liquidation_value: string | null;
  accumulated_depreciation: string;
  residual_value: string;
  useful_life_months: number | null;
  elapsed_depreciation_periods: number | null;
  commissioning_date: string | null;
  status: string;
  department_id: string | null;
  department_name: string | null;
  responsible_employee_id: string | null;
  responsible_name: string | null;
  expense_account_code: string | null;
  comment: string | null;
  external_source: string | null;
  write_off_method: string | null;
  track_mode: string | null;
  quantity: number | null;
}

export interface AssetInfoItem extends AssetRegistryItem {
  organization_name: string | null;
  commissioning_doc: string | null;
  retirement_doc: string | null;
  retirement_date: string | null;
  depreciation_in_system: string;
}

export interface AssetHistoryDoc {
  doc_type: string;
  doc_id: string | null;          // null — подія з 1С (events_1c)
  document_number: string;
  document_date: string;
  posted: boolean;
  note: string | null;
  amount: string | null;
  title?: string | null;          // назва документа 1С
}

export interface AssetDepreciationRow {
  document_id: string;
  document_number: string;
  period_year: number;
  period_month: number;
  posted: boolean;
  accumulated_before: string;
  amount: string;
  method_code: string | null;
}

export interface DepreciationStatementRow {
  depreciation_written_off: string;
  kind: AssetKind;
  id: string;
  inventory_number: string;
  name: string;
  method_name: string | null;
  group_name: string | null;
  department_name: string | null;
  responsible_name: string | null;
  commissioning_date: string | null;
  retirement_date: string | null;
  status: string;
  primary_cost: string;
  cost_start: string;
  cost_change: string;
  cost_end: string;
  depreciation_start: string;
  depreciation_period: string;
  depreciation_end: string;
  residual_end: string;
}

export interface LowValueBalanceRow {
  id: string;
  inventory_number: string;
  name: string;
  department_id: string | null;
  department_name: string | null;
  responsible_name: string | null;
  status: string;
  quantity: number;
  primary_cost: string;
  commissioning_date: string | null;
  retirement_date: string | null;
  opening_qty: number; in_qty: number; out_qty: number; closing_qty: number;
  opening_cost: string; in_cost: string; out_cost: string; closing_cost: string;
}

export interface DepreciationDocRow {
  doc_type: 'depreciation_calculation' | 'manual_operation';
  id: string; document_number: string; document_date: string;
  period_year: number; period_month: number; total: string;
}

export const assetsRegistry = {
  list: (params: {
    organization_id?: string; kind?: AssetKind; status?: string; department_id?: string;
    q?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) => api.get<Page<AssetRegistryItem>>('/refs/assets', { params }).then((r) => r.data),
  history: (kind: AssetKind, id: string) =>
    api.get<{ documents: AssetHistoryDoc[]; depreciation: AssetDepreciationRow[] }>(
      `/refs/assets/${kind}/${id}/history`).then((r) => r.data),
  info: (params: { organization_id: string; kind?: AssetKind; asset_id?: string; status?: string }) =>
    api.get<AssetInfoItem[]>('/refs/assets/info', { params }).then((r) => r.data),
  depreciationDocuments: (organization_id: string, params?: { asset_id?: string; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<{ items: DepreciationDocRow[]; total: number; limit: number; offset: number }>(
      '/reports/assets/depreciation-documents', { params: { organization_id, ...params, asset_id: params?.asset_id || undefined } }).then((r) => r.data),
  depreciationStatement: (params: {
    organization_id: string; date_from: string; date_to: string; kind?: 'FIXED_ASSET' | 'INTANGIBLE';
    asset_id?: string; document_ids?: string[]; limit?: number; offset?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
  }) => api.get<{ date_from: string; date_to: string; rows: DepreciationStatementRow[];
      total_rows: number; totals: Record<string, string> }>(
    '/reports/assets/depreciation-statement', {
      params, paramsSerializer: { indexes: null },
    }).then((r) => r.data),
  lowValueBalances: (params: {
    organization_id: string; date_from: string; date_to: string; department_id?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) => api.get<{ date_from: string; date_to: string; rows: LowValueBalanceRow[];
      total_rows: number; totals: Record<string, string> }>(
    '/reports/assets/low-value-balances', { params }).then((r) => r.data),
};

// ---------- Platform Admin: глобальні довідники НА ----------

export interface PlatformAssetGroup {
  id: string; code: string; asset_kind: 'FIXED_ASSET' | 'INTANGIBLE'; group_number: number;
  name_uk: string; min_useful_life_months: number | null;
  accounting_account_code: string | null; depreciation_account_code: string | null;
  is_active: boolean; seed_source: string | null;
}
export interface PlatformDepreciationMethod {
  id: string; code: string; name_uk: string; description: string | null;
  is_active: boolean; seed_source: string | null;
}

export const platformAssetRefs = {
  groups: () => api.get<PlatformAssetGroup[]>('/platform/asset-refs/groups').then((r) => r.data),
  createGroup: (p: Omit<PlatformAssetGroup, 'id' | 'seed_source'>) =>
    api.post<PlatformAssetGroup>('/platform/asset-refs/groups', p).then((r) => r.data),
  updateGroup: (id: string, p: Partial<Pick<PlatformAssetGroup,
    'name_uk' | 'min_useful_life_months' | 'accounting_account_code'
    | 'depreciation_account_code' | 'is_active'>>) =>
    api.patch<PlatformAssetGroup>(`/platform/asset-refs/groups/${id}`, p).then((r) => r.data),
  methods: () => api.get<PlatformDepreciationMethod[]>('/platform/asset-refs/methods').then((r) => r.data),
  createMethod: (p: Omit<PlatformDepreciationMethod, 'id' | 'seed_source'>) =>
    api.post<PlatformDepreciationMethod>('/platform/asset-refs/methods', p).then((r) => r.data),
  updateMethod: (id: string, p: Partial<Pick<PlatformDepreciationMethod,
    'name_uk' | 'description' | 'is_active'>>) =>
    api.patch<PlatformDepreciationMethod>(`/platform/asset-refs/methods/${id}`, p).then((r) => r.data),
};

export const ASSET_STATUS_UA: Record<string, string> = {
  PARKED: 'На складі (не введено)',
  OPERATING: 'В експлуатації',
  CONSERVATION: 'На консервації',
  REPAIR_LONG: 'У тривалому ремонті',
  WRITTEN_OFF: 'Списано',
  SOLD: 'Продано',
};
export const ASSET_KIND_UA: Record<AssetKind, string> = {
  FIXED_ASSET: 'ОЗ', INTANGIBLE: 'НМА', LOW_VALUE: 'МШП/МНМА',
};
export const DOC_TYPE_UA: Record<string, { label: string; route: string }> = {
  asset_commissioning: { label: 'Введення в експлуатацію', route: '/assets/commissioning' },
  asset_write_off: { label: 'Списання', route: '/assets/write-off' },
  asset_internal_transfer: { label: 'Внутрішнє переміщення', route: '/assets/transfer' },
  fixed_asset_inventory: { label: 'Інвентаризація', route: '/assets/inventory' },
  fixed_asset_modernization: { label: 'Модернізація', route: '/assets/state-change' },
  asset_state_change: { label: 'Зміна стану', route: '/assets/state-change' },
  fixed_asset_sale: { label: 'Продаж', route: '/assets/sale' },
  manual_operation: { label: 'Довідка (проводки з 1С)', route: '/manual-operations' },
  goods_sale: { label: 'Реалізація (продаж ОЗ)', route: '/goods-sales' },
};
